<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import Checkbox from 'primevue/checkbox'
import { ticketsetSchema, type TicketsetFormValues } from './ticketset-schema'
import TicketsetPricesTable from './TicketsetPricesTable.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import BInput from '@lib/components/base/BInput.vue'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import { sdk } from '@/graphql/client'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { buildTicketsetsCode } from '@/lib/configCode'
import { buildDeepLink } from '@/lib/deepLink'
import { useClipboardStore } from '@/stores/clipboard'
import type { Ticketset } from '@/composables/useTicketsetsData'

const props = defineProps<{ ticketset: Ticketset }>()
const emit = defineEmits<{ saved: []; cancel: [] }>()

const { t } = useI18n()
const toast = useToast()
const { confirm } = useConfirm()
const clipboard = useClipboardStore()

const qrCode = computed(() => buildDeepLink(buildTicketsetsCode([props.ticketset.id])))
const qrSubtitle = computed(() => {
    const ev = props.ticketset.event?.name ?? ''
    return ev ? `${ev} · ID ${props.ticketset.id}` : `ID ${props.ticketset.id}`
})

function addToClipboard() {
    clipboard.add({
        kind: 'ticketsets',
        item: {
            id: props.ticketset.id,
            name: props.ticketset.name,
            eventName: props.ticketset.event?.name ?? ''
        }
    })
    toast.success(t('ticketsets.qr.added'))
}

function initialValues(): TicketsetFormValues {
    return {
        name: props.ticketset.name ?? '',
        customName: props.ticketset.customName ?? '',
        validate: Boolean(props.ticketset.validate)
    }
}

const { handleSubmit, defineField, errors, resetForm, meta, values, setFieldValue } =
    useForm<TicketsetFormValues>({
        validationSchema: toTypedSchema(ticketsetSchema),
        initialValues: initialValues()
    })

const [name] = defineField('name')
const [_customName] = defineField('customName')
// BInput expects string | undefined; schema allows null → coerce
const customName = computed<string>({
    get: () => _customName.value ?? '',
    set: (v: string) => setFieldValue('customName', v)
})
const validate = computed({
    get: () => Boolean(values.validate),
    set: (v: boolean) => setFieldValue('validate', v)
})

const submitting = ref(false)

const onSubmit = handleSubmit(async vals => {
    submitting.value = true
    try {
        const r = await sdk.AdminUpdateTicketset({
            id: props.ticketset.id,
            name: vals.name,
            customName: vals.customName ?? '',
            validate: vals.validate
        })
        if (r.admin?.ticketset?.update?.ok) {
            toast.success(t('ticketsets.toast.saved'))
            resetForm({ values: vals })
            emit('saved')
        } else {
            toast.error(t('common.errors.generic'))
        }
    } catch (e) {
        toast.error((e as Error).message)
    } finally {
        submitting.value = false
    }
})

async function onCancel() {
    if (meta.value.dirty) {
        const ok = await confirm({
            title: t('ticketsets.confirm.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    emit('cancel')
}

watch(
    () => props.ticketset.id,
    () => resetForm({ values: initialValues() })
)

defineExpose({ isDirty: () => meta.value.dirty })
</script>

<template>
    <form class="space-y-6" @submit.prevent="onSubmit">
        <!-- Sekce 1: EDITOVAT -->
        <section>
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('ticketsets.form.section.edit') }}
            </h3>
            <div class="space-y-3">
                <label class="block">
                    <span class="text-xs font-semibold text-gray-600 mb-1 block">
                        {{ t('ticketsets.form.name') }} *
                    </span>
                    <BInput v-model="name" :invalid="!!errors.name" />
                    <span v-if="errors.name" class="text-xs text-red mt-1 block">{{ errors.name }}</span>
                </label>
                <label class="block">
                    <span class="text-xs font-semibold text-gray-600 mb-1 block">
                        {{ t('ticketsets.form.customName') }}
                    </span>
                    <BInput v-model="customName" :invalid="!!errors.customName" />
                    <span v-if="errors.customName" class="text-xs text-red mt-1 block">{{
                        errors.customName
                    }}</span>
                </label>
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                        :binary="true"
                        :model-value="validate"
                        @update:model-value="(v: boolean) => (validate = v)"
                    />
                    <span class="text-xs text-gray-700">{{ t('ticketsets.form.validate') }}</span>
                </label>
            </div>
        </section>

        <!-- Sekce 2: METADATA (read-only) -->
        <section class="border-t border-gray-200 pt-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('ticketsets.form.section.metadata') }}
            </h3>
            <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('ticketsets.metadata.event') }}</dt>
                    <dd class="text-ink">{{ props.ticketset.event?.name ?? '—' }}</dd>
                </div>
                <div v-if="props.ticketset.prefix" class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('ticketsets.metadata.prefix') }}</dt>
                    <dd class="text-ink font-mono">{{ props.ticketset.prefix }}</dd>
                </div>
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('ticketsets.metadata.entries') }}</dt>
                    <dd class="text-ink tabular-nums">{{ props.ticketset.entries }}</dd>
                </div>
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('ticketsets.metadata.entryPause') }}</dt>
                    <dd class="text-ink tabular-nums">{{ props.ticketset.entryPause }}</dd>
                </div>
                <div class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('ticketsets.metadata.unlimited') }}</dt>
                    <dd class="text-ink">
                        {{ props.ticketset.unlimited ? t('ticketsets.metadata.yes') : t('ticketsets.metadata.no') }}
                    </dd>
                </div>
                <div v-if="props.ticketset.bzucoId > 0" class="flex">
                    <dt class="text-gray-500 w-32 shrink-0">{{ t('ticketsets.metadata.bzucoId') }}</dt>
                    <dd class="text-ink tabular-nums">{{ props.ticketset.bzucoId }}</dd>
                </div>
            </dl>
        </section>

        <!-- Sekce 3: CENOVÉ HLADINY -->
        <section class="border-t border-gray-200 pt-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('ticketsets.form.section.prices') }}
            </h3>
            <TicketsetPricesTable :prices="props.ticketset.prices" />
        </section>

        <!-- Sekce 4: KONFIGURAČNÍ QR -->
        <section class="border-t border-gray-200 pt-4">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                {{ t('ticketsets.qr.title') }}
            </h3>
            <div class="flex items-start gap-4 flex-wrap">
                <QrPreview
                    :code="qrCode"
                    :label="props.ticketset.name"
                    :subtitle="qrSubtitle"
                    :size="160"
                />
                <BBtn severity="secondary" size="small" type="button" @click="addToClipboard">
                    {{ t('ticketsets.qr.addToClipboard') }}
                </BBtn>
            </div>
        </section>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
            <BBtn severity="secondary" size="small" type="button" @click="onCancel">
                {{ t('common.actions.cancel') }}
            </BBtn>
            <BBtn size="small" type="submit" :disabled="!meta.valid || !meta.dirty || submitting">
                {{ t('common.actions.save') }}
            </BBtn>
        </div>
    </form>
</template>
